import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useMeasure from "react-use-measure";
import { DateTime } from "luxon";
import { ZoomDomain } from "victory";
import { useParsedSearchParams } from "@fidelix/fx-miranda";
import { useGraphicsContext } from "../graphics-context";
import { GraphicHistoryItem } from "../graphics.queries";
import { removeMillisFromDate } from "./history-replay.utils";
import { useHistoryData } from "./history-replay-cached-data.utils";
import { useHistoryResolutionToast } from "./history-resolution-toast";
type HTMLOrSVGElement = HTMLElement | SVGElement;

export type BrushChange = {
  domain: [DateTime, DateTime];
  position: { x1: number; x2: number };
};

type BrushPosition = { left: number; right: number; width: number } | undefined;
export type HistoryReplayContextProps = {
  timeLineMeasureRef: (element: HTMLOrSVGElement | null) => void;
  timeLineContainerSize: { width: number; height: number };
  historyIsFetching: boolean;
  previewDataIsFetching?: boolean;
  previewData: GraphicHistoryItem | undefined;
  fromInSeconds: number;
  toInSeconds: number;
  isPlaying: boolean;
  togglePlayMode: () => void;
  movePlayheadTo: (position: number) => void;
  isLoading: boolean;
  zoomDomain?: ZoomDomain | undefined;
  onZoomDomainChange: (domain: ZoomDomain) => void;
  timelineDomainInSeconds: [number, number];
  isError: boolean;
  setDomain: (domain: [DateTime, DateTime]) => void;
  playbackSpeed: number;
  setPlaybackSpeed: (speed: number) => void;
  brushPosition: React.RefObject<BrushPosition>;
};

// Playhead position/data change up to ~10 times per second while playing.
// Kept in a separate context to minimize re-renders of components
export type HistoryReplayPlayheadContextProps = {
  playheadXInSeconds: number;
  historyData: GraphicHistoryItem | undefined;
  translatePlayhead: (pixels: number) => void;
};

const HistoryReplayContext = createContext<HistoryReplayContextProps | null>(
  null,
);
const HistoryReplayPlayheadContext =
  createContext<HistoryReplayPlayheadContextProps | null>(null);

export const defaultReplayTimePeriodMillis = 24 * 60 * 60 * 1000 - 1;

export function HistoryReplayDataProvider({ children }) {
  const { parsedParams, setSearchParams } = useParsedSearchParams({
    to: { type: "string", defaultValue: undefined },
    from: { type: "string", defaultValue: undefined },
    speed: { type: "number", defaultValue: 1 },
  });

  const [timeLineMeasureRef, { width, height }] = useMeasure();
  const [zoomDomain, setZoomDomain] = useState<ZoomDomain>();
  const { pointsQuery, attachment, element, cachedDataService } =
    useGraphicsContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const brushPosition = useRef<BrushPosition>(undefined);
  const timeLineContainerSize = useMemo(
    () => ({ width, height }),
    [width, height],
  );

  // Convert dates into seconds. These are needed to calculate different position
  const fromInSeconds = DateTime.fromISO(parsedParams.from!).toSeconds();
  const toInSeconds = DateTime.fromISO(parsedParams.to!).toSeconds();
  const timelineDomainInSeconds: [number, number] = useMemo(
    () => [
      zoomDomain?.x[0]
        ? DateTime.fromJSDate(zoomDomain?.x[0] as Date).toSeconds()
        : fromInSeconds,
      zoomDomain?.x[1]
        ? DateTime.fromJSDate(zoomDomain?.x[1] as Date).toSeconds()
        : toInSeconds,
    ],
    [zoomDomain, fromInSeconds, toInSeconds],
  );
  const [playheadXInSeconds, setPlayheadXInSeconds] = useState(fromInSeconds);
  const playbackSpeed = !isNaN(parsedParams.speed) ? parsedParams.speed : 1;

  const { historyData, historyIsError, historyIsFetching, historyResolution } =
    useHistoryData({
      timestampInSeconds: playheadXInSeconds,
      pointIds: pointsQuery.data?.points.map((p) => p.id),
      isPlaying,
      playbackSpeed,
    });

  useHistoryResolutionToast(historyResolution);

  // setSearchParams' identity isn't guaranteed stable across renders. Keep the
  // latest version in a ref so callbacks below can stay referentially stable
  // themselves (see stableValue memoization further down) without depending
  // on it directly.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Called when user changes the graphic picture
  // Reset timelines and the position of the playhead
  // Also pause the player
  useEffect(() => {
    if (parsedParams.from !== undefined) {
      return;
    }
    setIsPlaying(false);
    const date = DateTime.now();
    const newTimeLine: [DateTime, DateTime] = [
      date.minus({ milliseconds: defaultReplayTimePeriodMillis }),
      date,
    ];
    setPlayheadXInSeconds(date.toSeconds());
    setDomain(newTimeLine);
    setZoomDomain(undefined);
    setPlaybackSpeed(1);
    brushPosition.current = undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onZoomDomainChange = useCallback((domain: ZoomDomain | undefined) => {
    setZoomDomain(domain);
  }, []);

  const setDomain = useCallback((domain: [DateTime, DateTime]) => {
    setSearchParamsRef.current(
      (params) => {
        params.set("from", removeMillisFromDate(domain[0]).toISO());
        params.set("to", removeMillisFromDate(domain[1]).toISO());
      },
      { replace: true },
    );
    brushPosition.current = undefined;
  }, []);

  const togglePlayMode = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const translatePlayhead = useCallback((amount: number) => {
    setPlayheadXInSeconds((prev) => prev + amount);
  }, []);

  const movePlayheadTo = useCallback((position: number) => {
    setPlayheadXInSeconds(position);
  }, []);

  const setPlaybackSpeed = useCallback((speed: number) => {
    setSearchParamsRef.current(
      (params) => {
        params.set("speed", String(speed));
      },
      { replace: true },
    );
  }, []);

  const isLoading =
    historyIsFetching ||
    pointsQuery.isFetching ||
    attachment === undefined ||
    element === undefined;

  const isError = historyIsError || pointsQuery.isError;

  const stableValue = useMemo<HistoryReplayContextProps>(
    () => ({
      timeLineMeasureRef,
      timeLineContainerSize,
      historyIsFetching,
      previewDataIsFetching: cachedDataService?.isFetching,
      previewData: cachedDataService?.getPreviewDataFromSelectedPoints(),
      fromInSeconds,
      toInSeconds,
      isPlaying,
      togglePlayMode,
      movePlayheadTo,
      isLoading,
      onZoomDomainChange,
      zoomDomain,
      isError,
      setDomain,
      playbackSpeed,
      setPlaybackSpeed,
      brushPosition,
      timelineDomainInSeconds,
    }),
    [
      timeLineMeasureRef,
      timeLineContainerSize,
      historyIsFetching,
      cachedDataService,
      fromInSeconds,
      toInSeconds,
      isPlaying,
      togglePlayMode,
      movePlayheadTo,
      isLoading,
      onZoomDomainChange,
      zoomDomain,
      isError,
      setDomain,
      playbackSpeed,
      setPlaybackSpeed,
      brushPosition,
      timelineDomainInSeconds,
    ],
  );

  const playheadValue = useMemo<HistoryReplayPlayheadContextProps>(
    () => ({ playheadXInSeconds, historyData, translatePlayhead }),
    [playheadXInSeconds, historyData, translatePlayhead],
  );

  return (
    <HistoryReplayContext.Provider value={stableValue}>
      <HistoryReplayPlayheadContext.Provider value={playheadValue}>
        {children}
      </HistoryReplayPlayheadContext.Provider>
    </HistoryReplayContext.Provider>
  );
}

export function useHistoryReplayContext() {
  const value = useContext(HistoryReplayContext);
  if (!value) {
    throw new Error(
      "useHistoryReplayContext has to be used under HistoryReplayDataProvider",
    );
  }
  return value;
}

export function useHistoryReplayPlayheadContext() {
  const value = useContext(HistoryReplayPlayheadContext);
  if (!value) {
    throw new Error(
      "useHistoryReplayPlayheadContext has to be used under HistoryReplayDataProvider",
    );
  }
  return value;
}
